import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GameStateResponse } from '@/lib/api';

interface UseAdminGameStatesReturn {
  states: GameStateResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminGameStates(userId: string): UseAdminGameStatesReturn {
  const [states, setStates] = useState<GameStateResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchStates = useCallback(async () => {
    if (!userId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.adminListGameStates(userId, { limit: 100 });
      if (fetchIdRef.current !== id) return;
      setStates(result.states);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load game states');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [userId]);

  const refresh = useCallback(() => {
    setError(null);
    fetchStates();
  }, [fetchStates]);

  useEffect(() => {
    if (userId) {
      refresh();
    } else {
      setStates([]);
      setTotal(0);
      setLoading(false);
    }
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh, userId]);

  return { states, total, loading, error, refresh };
}
